  // dev-portal/src/pages/admin/AdminDashboard.js

  import React, { useState, useEffect } from 'react';
  import { api, endpoints, syncHelpers } from '../../api/client';
  import { RefreshCw, Users, Package, Database, Activity } from 'lucide-react';

  // Helper to format snake_case to Title Case
  const formatKey = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper to capitalize first letter
  const capitalize = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  export default function AdminDashboard() {
    const [syncStatus, setSyncStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [systemHealth, setSystemHealth] = useState(null);

    useEffect(() => {
      fetchSyncStatus();
      checkSystemHealth();
    }, []);

    const fetchSyncStatus = async () => {
      try {
        const response = await syncHelpers.getSyncStatus();
        setSyncStatus(response.data);
      } catch (error) {
        console.error('Failed to fetch sync status:', error);
      }
    };

    const checkSystemHealth = async () => {
      try {
        const response = await api.get(endpoints.adminSync.health);
        setSystemHealth(response.data);
      } catch (error) {
        console.error('Failed to check system health:', error);
      }
    };

    const handleSync = async (syncType, message) => {
      setLoading(true);
      try {
        const response = await syncHelpers.triggerSync(syncType);
        alert(response.data.message || message);
        
        // Refresh status after 2 seconds
        setTimeout(() => {
          fetchSyncStatus();
        }, 2000);
      } catch (error) {
        alert('Sync failed: ' + (error.response?.data?.detail || error.message));
      } finally {
        setLoading(false);
      }
    };

    const startEventPolling = async () => {
      try {
        const response = await syncHelpers.startEventPolling(60);
        alert(response.data.message || 'Event polling started');
        fetchSyncStatus();
      } catch (error) {
        alert('Failed to start polling: ' + (error.response?.data?.detail || error.message));
      }
    };

    const stopEventPolling = async () => {
      try {
        const response = await syncHelpers.stopEventPolling();
        alert(response.data.message || 'Event polling stopped');
        fetchSyncStatus();
      } catch (error) {
        alert('Failed to stop polling: ' + (error.response?.data?.detail || error.message));
      }
    };

    const syncInternalSystem = async () => {
      setLoading(true);
      try {
        const response = await syncHelpers.loadInternalData();
        alert(response.data.message || 'Internal sync initiated');
      } catch (error) {
        alert('Internal sync failed: ' + (error.response?.data?.detail || error.message));
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-2xl sm:text-3xl font-bold">Admin Dashboard</h1>
          <button
            onClick={fetchSyncStatus}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh Status
          </button>
        </div>

        {/* System Health */}
        {systemHealth && (
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Activity className="w-5 h-5" />
              System Health
            </h3>
          {/* 2 cols on mobile → 4 on desktop */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(systemHealth.components).map(([key, value]) => (
                <div key={key} className="text-sm">
                  <span className="font-medium">{formatKey(key)}:</span>
                  <span className={`ml-2 ${value.status === 'healthy' ? 'text-green-600' : 'text-red-600'}`}>
                    {capitalize(value.status) || value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sync Status Overview */}
        {syncStatus && (
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="font-semibold mb-2">Sync Status Overview</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-600">Legacy Users Synced:</p>
                <p className="font-medium">{syncStatus.legacy?.users_synced || 0}</p>
              </div>
              <div>
                <p className="text-gray-600">Event Polling:</p>
                <p className="font-medium">
                  {syncStatus.legacy?.polling_active ? 'Active' : 'Inactive'}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Last Internal Sync:</p>
                <p className="font-medium">
                  {syncStatus.internal?.last_sync?.completed_at 
                    ? new Date(syncStatus.internal.last_sync.completed_at).toLocaleString()
                    : 'Never'}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Internal Sync Status:</p>
                <p className="font-medium">
                  {capitalize(syncStatus.internal?.last_sync?.status) || 'Never Synced'}
                </p>
              </div>
            </div>
          </div>
        )}

        <p className="font-semibold text-gray-700">Quick Actions</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div className="p-6 bg-white shadow rounded-lg">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Manage Developers
            </h3>
            <ul className="list-disc pl-6 text-sm text-gray-700 mb-4">
              <li>View & Approve Developer Accounts</li>
              <li>Reset User Passwords</li>
              <li>Monitor API Keys</li>
            </ul>
            <a
              href="/admin/users"
              className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Manage Users
            </a>
          </div>

          <div className="p-6 bg-white shadow rounded-lg">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Manage Apps
            </h3>
            <ul className="list-disc pl-6 text-sm text-gray-700 mb-4">
              <li>Review Submitted Apps</li>
              <li>Approve or Suspend Apps</li>
              <li>Monitor Webhook Logs</li>
            </ul>
            <a
              href="/admin/apps"
              className="inline-block px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Manage Apps
            </a>
          </div>
        </div>
        
        {/* System Sync Controls */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Database className="w-5 h-5" />
            System Sync Controls
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Legacy System */}
            <div className="border border-gray-200 p-4 rounded-lg">
              <h4 className="font-medium mb-3 text-purple-700">Legacy System</h4>
              <div className="space-y-2">
                <button
                  onClick={() => handleSync('legacy_users', 'Legacy user sync initiated')}
                  disabled={loading}
                  className="w-full bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 disabled:opacity-50"
                >
                  Sync Legacy Users
                </button>
                
                <button
                  onClick={() => handleSync('legacy_events', 'Legacy event sync initiated')}
                  disabled={loading}
                  className="w-full bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 disabled:opacity-50"
                >
                  Sync Legacy Events Once
                </button>
                
                <div className="flex gap-2">
                  <button
                    onClick={startEventPolling}
                    disabled={loading || syncStatus?.legacy?.polling_active}
                    className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50"
                  >
                    Start Polling
                  </button>
                  <button
                    onClick={stopEventPolling}
                    disabled={loading || !syncStatus?.legacy?.polling_active}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                  >
                    Stop Polling
                  </button>
                </div>
              </div>
            </div>
            
            {/* Internal System */}
            <div className="border border-gray-200 p-4 rounded-lg">
              <h4 className="font-medium mb-3 text-blue-700">Internal System</h4>
              <div className="space-y-2">
                <button
                  onClick={syncInternalSystem}
                  disabled={loading}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
                  Sync All Internal Data
                </button>
                
                <button
                  onClick={() => handleSync('internal_features', 'Feature sync initiated')}
                  disabled={loading}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Sync Features Only
                </button>
                
                <button
                  onClick={() => handleSync('internal_events', 'Event sync initiated')}
                  disabled={loading}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Sync Events Only
                </button>
                
                <a
                  href="/admin/internal-sync"
                  className="block w-full text-center bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                >
                  Manage Feature Links
                </a>
              </div>
            </div>
          </div>
          
          {/* Full System Sync */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => handleSync('full_sync', 'Full system sync initiated')}
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-3 rounded hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 font-medium"
            >
              Run Full System Sync (All Systems)
            </button>
          </div>
        </div>

        {/* Queue Management */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="font-semibold mb-3">Queue Management</h3>
          <div className="flex gap-2">
            <a
              href="/admin/queue"
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              View Queue Status
            </a>
            <a
              href="/admin/logs"
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              View Webhook Logs
            </a>
          </div>
        </div>
      </div>
    );
  }